import { useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useSnackbar } from 'notistack';
import { useGenerateStartList } from '../../api/hooks/useStartList';
import { ConfirmDialog } from '../ConfirmDialog';
import type { CollisionReport } from '../../types/startList';

interface Props {
  groupId: number;
  hasExistingStartList: boolean;
  /** Callback po uspesne generaci — predava varovani z resolveru. */
  onGenerated?: (warnings: CollisionReport[]) => void;
}

export function StartListGenerateButton({ groupId, hasExistingStartList, onGenerated }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const generateMut = useGenerateStartList(groupId);
  const [confirming, setConfirming] = useState(false);
  const [warnings, setWarnings] = useState<CollisionReport[]>([]);

  const doGenerate = () => {
    setConfirming(false);
    generateMut.mutate(undefined, {
      onSuccess: (data) => {
        const u = data.unresolvedCollisions ?? [];
        setWarnings(u);
        if (u.length === 0) {
          enqueueSnackbar('Startovka vygenerovana', { variant: 'success' });
        } else {
          enqueueSnackbar(`Startovka vygenerovana s ${u.length} varovanimi`, { variant: 'warning' });
        }
        onGenerated?.(u);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        enqueueSnackbar(e?.response?.data?.message ?? 'Chyba pri generovani', { variant: 'error' });
      },
    });
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={() => {
            if (hasExistingStartList) setConfirming(true);
            else doGenerate();
          }}
          disabled={generateMut.isPending}
        >
          {generateMut.isPending ? 'Generuji…' : 'Vygenerovat startovku'}
        </Button>
      </Stack>

      {warnings.length > 0 && (
        <Alert severity="warning">
          Algoritmus nedokazal vyresit {warnings.length}{' '}
          {warnings.length === 1 ? 'kolizi stejneho druzstva v rozbehu' : 'kolizi stejneho druzstva v rozbezich'}.
          Vyreste je rucne pomoci drag&drop. Postizene rozbehy:{' '}
          {warnings
            .map((w) => (w.heatNumber > 0 ? `#${w.heatNumber}` : 'limit iteraci'))
            .join(', ')}
          .
        </Alert>
      )}

      <ConfirmDialog
        open={confirming}
        title="Pregenerovat startovku?"
        message="Stavajici startovka bude prepsana. Pokracovat?"
        confirmText="Pregenerovat"
        destructive
        loading={generateMut.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={doGenerate}
      />
    </Stack>
  );
}
